// apiError — map an API failure to the user-facing message (errors.json keys
// ARE the backend codes; GENERIC fallback), quoting what the envelope's
// `details` names (who or how many stand in the way). The one home for the
// error-envelope contract: non-form mutations (pill flips, confirm-dialog verbs,
// tolerance saves), the form shell and every action menu's inline line call it —
// a surface never keeps a copy of the mapping (the proxy's answer for an absent
// server is told here, once), nor reads the envelope by hand: the code a branch
// turns on is `apiErrorCode`, what it acts on beyond the words `apiErrorDetails`.
import { AxiosError } from 'axios';
import type { TFunction } from 'i18next';
import { codeOf, isServerUnavailable, refusedResponseOf } from './refusal.js';
import { formatList, formatNumber } from './format.js';

/** The typed code an API failure carries (the error envelope), or null. */
export const apiErrorCode = (err: unknown): string | null => codeOf(err);

/** The envelope's `details` as sent — what a surface acts on beyond the words (who stands in the way, what is
 *  missing, how many); `{}` when the failure carries none. */
export function apiErrorDetails(err: unknown): Record<string, unknown> {
  return asDetails((refusedResponseOf(err)?.data as { details?: unknown } | null | undefined)?.details);
}

/** The envelope's `details` is a record (`ErrorResponseSchema`) — anything else reads as none. */
function asDetails(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : {};
}

// i18next reads these as options, never as values — a detail of that name is not quoted.
const I18N_OPTIONS = new Set(['lng', 'lngs', 'ns', 'context', 'defaultValue', 'returnObjects', 'joinArrays', 'replace', 'interpolation', 'postProcess', 'ordinal']);

/** The values a message may quote from the envelope's `details`: a word as it is, a number in the reader's digits
 *  (`count` raw — it picks the plural form — with `countText`, the same number in the reader's digits, for the words to
 *  print), a list of names joined the reader's way. */
export function apiErrorValues(err: unknown): Record<string, string | number> {
  const out: Record<string, string | number> = {};
  for (const [key, value] of Object.entries(apiErrorDetails(err))) {
    if (I18N_OPTIONS.has(key)) continue;
    if (typeof value === 'string') out[key] = value;
    else if (typeof value === 'number') {
      out[key] = key === 'count' ? value : formatNumber(value);
      if (key === 'count') out.countText = formatNumber(value);
    }
    else if (Array.isArray(value) && value.every((v) => typeof v === 'string')) out[key] = formatList(value);
  }
  return out;
}

export function apiErrorMessage(err: unknown, tErrors: TFunction): string {
  const code = apiErrorCode(err);
  if (code) return tErrors(code, { ...apiErrorValues(err), defaultValue: tErrors('GENERIC') });
  // No envelope at all: the proxy answered for an absent server — say so,
  // calmly, instead of "something went wrong" (which reads as our fault).
  return isServerUnavailable(err) ? tErrors('SERVER_UNAVAILABLE') : tErrors('GENERIC');
}

/** The error envelope of a FILE request (`responseType: 'blob'`): a refusal's
 *  JSON body arrives as a Blob there, so the typed code (and the numbers the
 *  screen quotes, `details`) is read out of it. Null when there is none. */
export async function apiErrorEnvelope(err: unknown): Promise<{ code: string; details: Record<string, unknown> } | null> {
  if (!(err instanceof AxiosError)) return null;
  let body: unknown = err.response?.data;
  if (body instanceof Blob) {
    try {
      body = JSON.parse(await body.text());
    } catch {
      return null;
    }
  }
  const envelope = body as { code?: unknown; details?: unknown } | undefined;
  if (typeof envelope?.code !== 'string') return null;
  return { code: envelope.code, details: asDetails(envelope.details) };
}
